import { AvroDataset, compressionCodecType, Dataset, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { AvroSettings } from 'app/model/dataflow/dataset-settings/avro-settings';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { getAdlsDatasetForPreview, ensureAllPathsAreUnique, processEachADLSEntity, initializeCommonConnectionProperties } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IDataMapperFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class AvroDatasetTypeHandler extends DatasetTypeHandler {
    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('AvroSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as AvroSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as AvroSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as AvroDataset)?.location?.getLocation()?.container;
                if (!containerPath) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, this.isSource ? stringResources.DuplicateSourceEntitySelected : stringResources.DuplicateTargetEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as AvroDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);

        });

        return tables;
    }

    public getFileOptions(): IDataMapperFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.deflate,
                    value: compressionCodecType.deflate
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                }
            ]
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const avroSettings: AvroSettings = Owned.create<AvroSettings>(AvroSettings.MODEL, dataFlowSource);
        avroSettings.initialize(undefined);
        return avroSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const compressionType = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);

        // Create and populate AvroSettings
        const avroSettings = Owned.create<AvroSettings>(AvroSettings.MODEL, table as DataFlowSource);
        avroSettings.initialize(undefined);

        avroSettings.container = createDataflowExpression(container) as DataFlowExpression;
        avroSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        avroSettings.compressionType = compressionType ?? undefined;

        return avroSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Avro, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add avro dataset settings
        const avroSettings = selectedSourceFileOptions as AvroSettings;
        if (this.isSource && !!avroSettings.compressionType) {
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, avroSettings.compressionType);
        }
    }
}
